import { Component } from "../../widgets/component";
import { IComponent } from "../../interfaces/iComponent";
import { ColumnGroup } from "../../entities/columnGroup";
import { ColumnApi } from "../../columnController/columnApi";
import { ColumnController } from "../../columnController/columnController";
import { Autowired } from "../../context/context";
import { TouchListener } from "../../widgets/touchListener";
import { RefSelector } from "../../widgets/componentAnnotations";
import { OriginalColumnGroup } from "../../entities/originalColumnGroup";
import { GridApi } from "../../gridApi";
import { escapeString } from "../../utils/string";
import { isStopPropagationForDyGrid, stopPropagationForDyGrid } from "../../utils/event";
import { setDisplayed } from "../../utils/dom";
import { createIconNoSpan } from "../../utils/icon";
import { exists } from "../../utils/generic";

export interface IHeaderGroupParams {
  columnGroup: ColumnGroup;
  displayName: string;
  setExpanded: (expanded: boolean) => void;
  api: GridApi;
  columnApi: ColumnApi;
  context: any;
}

export interface IHeaderGroup { }

export interface IHeaderGroupComp extends IHeaderGroup, IComponent<IHeaderGroupParams> { }

export class HeaderGroupComp extends Component implements IHeaderGroupComp {

  @Autowired("columnController") private columnController: ColumnController;

  static TEMPLATE = /* html */
    `<div class="dy-header-group-cell-label" ref="dyContainer" role="presentation">
            <span ref="dyLabel" class="dy-header-group-text" role="presentation"></span>
            <span ref="dyOpened" class="dy-header-icon dy-header-expand-icon dy-header-expand-icon-expanded"></span>
            <span ref="dyClosed" class="dy-header-icon dy-header-expand-icon dy-header-expand-icon-collapsed"></span>
        </div>`;

  private params: IHeaderGroupParams;

  @RefSelector("dyOpened") private eOpenIcon: HTMLElement;
  @RefSelector("dyClosed") private eCloseIcon: HTMLElement;

  constructor() {
    super(HeaderGroupComp.TEMPLATE);
  }

  // this is a user component, and IComponent has "public destroy()" as part of the interface.
  // so we need to override destroy() just to make the method public.
  public destroy(): void {
    super.destroy();
  }

  public init(params: IHeaderGroupParams): void {
    this.params = params;

    this.setupLabel();
    this.addGroupExpandIcon();
    this.setupExpandIcons();
  }

  private setupExpandIcons(): void {
    this.addInIcon("columnGroupOpened", "dyOpened");
    this.addInIcon("columnGroupClosed", "dyClosed");

    const expandAction = (event: MouseEvent) => {
      if (isStopPropagationForDyGrid(event)) {
        return;
      }

      const newExpandedValue = !this.params.columnGroup.isExpanded();
      this.columnController.setColumnGroupOpened(this.params.columnGroup.getOriginalColumnGroup(), newExpandedValue, "uiColumnExpanded");
    };

    this.addTouchAndClickListeners(this.eCloseIcon, expandAction);
    this.addTouchAndClickListeners(this.eOpenIcon, expandAction);

    const stopPropagationAction = (event: MouseEvent) => {
      stopPropagationForDyGrid(event);
    };

    this.addManagedListener(this.eCloseIcon, "dblclick", stopPropagationAction);
    this.addManagedListener(this.eOpenIcon, "dblclick", stopPropagationAction);

    this.addManagedListener(this.getGui(), "dblclick", expandAction);

    this.updateIconVisibility();

    const originalColumnGroup = this.params.columnGroup.getOriginalColumnGroup();
    this.addManagedListener(originalColumnGroup, OriginalColumnGroup.EVENT_EXPANDED_CHANGED, this.updateIconVisibility.bind(this));
    this.addManagedListener(originalColumnGroup, OriginalColumnGroup.EVENT_EXPANDABLE_CHANGED, this.updateIconVisibility.bind(this));
  }

  private addTouchAndClickListeners(eElement: HTMLElement, action: (event: MouseEvent) => void): void {
    const touchListener = new TouchListener(eElement, true);

    this.addManagedListener(touchListener, TouchListener.EVENT_TAP, action);
    this.addDestroyFunc(() => touchListener.destroy());
    this.addManagedListener(eElement, "click", action);
  }

  private updateIconVisibility(): void {
    const columnGroup = this.params.columnGroup;
    if (columnGroup.isExpandable()) {
      const expanded = this.params.columnGroup.isExpanded();
      setDisplayed(this.eOpenIcon, expanded);
      setDisplayed(this.eCloseIcon, !expanded);
    } else {
      setDisplayed(this.eOpenIcon, false);
      setDisplayed(this.eCloseIcon, false);
    }
  }

  private addInIcon(iconName: string, refName: string): void {
    const eIcon = createIconNoSpan(iconName, this.gridOptionsWrapper, null);
    if (eIcon) {
      this.getRefElement(refName).appendChild(eIcon);
    }
  }

  private addGroupExpandIcon() {
    if (!this.params.columnGroup.isExpandable()) {
      setDisplayed(this.eOpenIcon, false);
      setDisplayed(this.eCloseIcon, false);
      return;
    }
  }

  private setupLabel(): void {
    // no renderer, default text render
    const displayName = this.params.displayName;

    if (exists(displayName)) {
      const displayNameSanitised = escapeString(displayName);
      this.getRefElement('dyLabel').innerHTML = displayNameSanitised!;
    }
  }
}
